import type { ReactNode } from 'react';
import { Alert, Button } from '@meterup/atto';
import { getErrorMessage, isDefined } from '@meterup/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { postFloorPlan } from '../../api/devices_api';
import {
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogControls,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
  DialogTrigger,
} from '../Dialog/Dialog';
import { FilePickerInput } from './FilePickerInput';

export function UploadFloorPlanDialog({
  trigger,
  controllerName,
}: {
  controllerName: string;
  trigger: ReactNode;
}) {
  const [fileForUpload, setFileForUpload] = useState<File | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient();

  const uploadFileMutation = useMutation(
    async (file: File) => {
      await postFloorPlan(controllerName, file);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['controller', controllerName]);
        setIsOpen(false);
        setFileForUpload(null);
      },
    },
  );

  const uploadFile = async (e: React.FormEvent<EventTarget>) => {
    e.preventDefault();
    if (isDefined(fileForUpload)) {
      uploadFileMutation.mutate(fileForUpload);
    }
  };

  const errorMessage = isDefined(uploadFileMutation.error)
    ? getErrorMessage(uploadFileMutation.error)
    : null;

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setFileForUpload(null);
      uploadFileMutation.reset();
    }
    setIsOpen(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <form onSubmit={uploadFile}>
          <DialogHeader>
            <DialogTitle>Upload floor plan</DialogTitle>
            <DialogControls>
              <DialogCloseButton />
            </DialogControls>
          </DialogHeader>
          {isDefined(errorMessage) && (
            <Alert
              icon="warning"
              variant="negative"
              heading="Upload failed"
              copy={errorMessage}
              relation="stacked"
            />
          )}
          <DialogSection>
            <FilePickerInput
              accept="image/x-png,image/jpeg,image/jpg"
              value={fileForUpload}
              onChange={setFileForUpload}
            />
          </DialogSection>
          <DialogFooter>
            <DialogControls>
              <DialogClose asChild>
                <Button variant="secondary">Cancel</Button>
              </DialogClose>
              <Button
                loading={uploadFileMutation.isLoading}
                disabled={!isDefined(fileForUpload)}
                type="submit"
              >
                Upload
              </Button>
            </DialogControls>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
